import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, Calendar, MapPin, Camera, Dog, ArrowLeft, ArrowRight, Globe,
  Users, Grid3X3, ShoppingCart, X, Trash2, CreditCard, Check, Package,
} from 'lucide-react';
import axios from 'axios';
import SportGalleryToolbar from '../components/sport/SportGalleryToolbar';
import SportPhotoGrid from '../components/sport/SportPhotoGrid';
import SportLightbox from '../components/sport/SportLightbox';
import useFavorites from '../hooks/useFavorites';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getCartId() {
  let id = localStorage.getItem('sport_cart_id');
  if (!id) {
    id = 'cart_' + Math.random().toString(36).slice(2, 14);
    localStorage.setItem('sport_cart_id', id);
  }
  return id;
}

export default function SportSeasonDetailPage() {
  const { seasonId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language, setLanguage } = useLanguage();
  const [season, setSeason] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get('tab') || 'photos');

  // Gallery state
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterRatings, setFilterRatings] = useState([]);
  const [filenameSearch, setFilenameSearch] = useState('');
  const [filterDogId, setFilterDogId] = useState('');
  const [filterTeamId, setFilterTeamId] = useState('');
  const [filterEventId, setFilterEventId] = useState('');
  const [filterPhotographerId, setFilterPhotographerId] = useState('');
  const [filterCompetitorId, setFilterCompetitorId] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [filterCamera, setFilterCamera] = useState('');
  const [filterBurstType, setFilterBurstType] = useState('');
  const [filterTimeStart, setFilterTimeStart] = useState('');
  const [filterTimeEnd, setFilterTimeEnd] = useState('');
  const [filterCompetitionDay, setFilterCompetitionDay] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Season data for filters
  const [dogs, setDogs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [cameras, setCameras] = useState([]);

  // Favorites
  const { favoriteIds, toggleFavorite } = useFavorites();

  // Cart state
  const cartId = getCartId();
  const [cart, setCart] = useState({ items: [], total: 0, items_count: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [addingToCart, setAddingToCart] = useState({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');

  const t = language === 'pl' ? {
    photos: 'zdjec', dogs: 'psow', teams: 'druzyn', events: 'zawodow',
    noPhotos: 'Brak zdjec', prev: 'Poprzednia', next: 'Nastepna',
    addToCart: 'Do koszyka', inCart: 'W koszyku', cart: 'Koszyk',
    dogPack: 'Pakiet', allPhotos: 'wszystkie zdjecia', discount: 'znizka',
    emptyCart: 'Koszyk jest pusty', total: 'Suma', checkout: 'Zamow',
    clearCart: 'Wyczysc', perPhoto: 'za zdjecie',
    buyerInfo: 'Dane kupujacego', name: 'Imie i nazwisko', email: 'Email',
    phone: 'Telefon', pay: 'Przejdz do platnosci', ordering: 'Przetwarzanie...',
    close: 'Zamknij', remove: 'Usun', items: 'szt.',
  } : {
    photos: 'photos', dogs: 'dogs', teams: 'teams', events: 'events',
    noPhotos: 'No photos', prev: 'Previous', next: 'Next',
    addToCart: 'Add to cart', inCart: 'In cart', cart: 'Cart',
    dogPack: 'Pack', allPhotos: 'all photos', discount: 'discount',
    emptyCart: 'Cart is empty', total: 'Total', checkout: 'Checkout',
    clearCart: 'Clear', perPhoto: 'per photo',
    buyerInfo: 'Buyer info', name: 'Full name', email: 'Email',
    phone: 'Phone', pay: 'Proceed to payment', ordering: 'Processing...',
    close: 'Close', remove: 'Remove', items: 'items',
  };

  // Fetch season + events
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/sport/public/seasons/${seasonId}/events`);
        setSeason(res.data.season);
        setEvents(res.data.events);
      } catch (err) {
        console.error('Failed to load season:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [seasonId]);

  // Fetch season-level filter data when photos tab is active
  useEffect(() => {
    if (tab !== 'photos' && tab !== 'dogs' && tab !== 'teams') return;

    const fetchFilterData = async () => {
      try {
        const [dogsRes, teamsRes, photRes, compRes, camRes] = await Promise.all([
          axios.get(`${API}/sport/public/seasons/${seasonId}/dogs`),
          axios.get(`${API}/sport/public/seasons/${seasonId}/teams`),
          axios.get(`${API}/sport/public/seasons/${seasonId}/photographers`),
          axios.get(`${API}/sport/public/seasons/${seasonId}/competitors`),
          axios.get(`${API}/sport/public/seasons/${seasonId}/cameras`),
        ]);
        setDogs(dogsRes.data.dogs || []);
        setTeams(teamsRes.data.teams || []);
        setPhotographers(photRes.data.photographers || []);
        setCompetitors(compRes.data.competitors || []);
        setCameras(camRes.data.cameras || []);
      } catch (err) {
        console.error('Failed to load filter data:', err);
      }
    };
    fetchFilterData();
  }, [seasonId, tab]);

  // Fetch photos
  const fetchPhotos = async (silent = false) => {
    if (tab !== 'photos' || !season) return;
    if (!silent) setPhotosLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize, sort: sortBy });
      if (filterEventId) params.set('event_id', filterEventId);
      if (filterDogId) params.set('dog_id', filterDogId);
      if (filterTeamId) params.set('team_id', filterTeamId);
      if (filterPhotographerId) params.set('photographer_id', filterPhotographerId);
      if (filterCompetitorId) params.set('competitor_id', filterCompetitorId);
      if (filterRatings.length > 0) params.set('ratings', filterRatings.join(','));
      if (filenameSearch) params.set('filename', filenameSearch);
      if (filterCompetitionDay) {
        params.set('date_start', filterCompetitionDay);
        params.set('date_end', filterCompetitionDay);
      } else {
        if (dateStart) params.set('date_start', dateStart);
        if (dateEnd) params.set('date_end', dateEnd);
      }
      if (filterCamera) params.set('camera', filterCamera);
      if (filterBurstType) params.set('burst_filter', filterBurstType);
      if (filterTimeStart) params.set('time_start', filterTimeStart);
      if (filterTimeEnd) params.set('time_end', filterTimeEnd);
      const res = await axios.get(`${API}/sport/public/seasons/${seasonId}/photos?${params}`);
      setPhotos(res.data.photos);
      setTotal(res.data.total);
    } catch (err) {
      if (!silent) console.error('Failed to load photos:', err);
    } finally {
      if (!silent) setPhotosLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [season, seasonId, tab, page, pageSize, sortBy, filterEventId, filterDogId, filterTeamId,
      filterPhotographerId, filterCompetitorId, filterRatings, filenameSearch, dateStart, dateEnd,
      filterCamera, filterBurstType, filterTimeStart, filterTimeEnd, filterCompetitionDay]);

  // Auto-refresh gallery every 15s to pick up new imports from FS
  useEffect(() => {
    if (tab !== 'photos' || !season) return;
    const interval = setInterval(() => fetchPhotos(true), 15000);
    return () => clearInterval(interval);
  }, [season, seasonId, tab, page, pageSize, sortBy, filterEventId, filterDogId, filterTeamId,
      filterPhotographerId, filterCompetitorId, filterRatings, filenameSearch, dateStart, dateEnd,
      filterCamera, filterBurstType, filterTimeStart, filterTimeEnd, filterCompetitionDay]);

  // Cart
  const refreshCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sport/shop/cart/${cartId}`);
      setCart(res.data);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }, [cartId]);

  useEffect(() => { refreshCart(); }, [refreshCart]);

  const cartPhotoIds = new Set(cart.items.map(i => i.photo_id));

  const handleAddToCart = async (photoId) => {
    // Need event_id for cart — get from photo
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;
    setAddingToCart(prev => ({ ...prev, [photoId]: true }));
    try {
      await axios.post(`${API}/sport/shop/cart/${cartId}/add`, {
        event_id: photo.event_id, photo_id: photoId,
      });
      await refreshCart();
    } catch (err) {
      console.error('Add to cart error:', err);
    } finally {
      setAddingToCart(prev => ({ ...prev, [photoId]: false }));
    }
  };

  const handleRemoveFromCart = async (photoId) => {
    try {
      await axios.delete(`${API}/sport/shop/cart/${cartId}/item/${photoId}`);
      await refreshCart();
    } catch (err) {
      console.error('Remove from cart error:', err);
    }
  };

  const handleClearCart = async () => {
    try {
      await axios.delete(`${API}/sport/shop/cart/${cartId}`);
      await refreshCart();
    } catch (err) {
      console.error('Clear cart error:', err);
    }
  };

  const handleCheckout = async () => {
    if (!buyerName || !buyerEmail) return;
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/sport/shop/cart/${cartId}/checkout`, {
        buyer_name: buyerName, buyer_email: buyerEmail, buyer_phone: buyerPhone,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!season) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">
          {language === 'pl' ? 'Sezon nie znaleziony' : 'Season not found'}
        </p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  const statusBadge = (status) => {
    const colors = {
      upcoming: 'bg-blue-500/10 text-blue-500',
      active: 'bg-green-500/10 text-green-500',
      completed: 'bg-gray-500/10 text-gray-500',
    };
    return colors[status] || colors.upcoming;
  };

  const totalSeasonPhotos = events.reduce((acc, e) => acc + (e.photo_count || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/sport" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Dog className="w-6 h-6" />
            <div>
              <h1 className="text-lg font-bold">{season.name}</h1>
              <p className="text-xs text-muted-foreground">
                {season.sport_type} — {season.year}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Cart button */}
            <Button
              variant={cart.items_count > 0 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCartOpen(true)}
              className="relative"
            >
              <ShoppingCart className="w-4 h-4" />
              {cart.items_count > 0 && (
                <span className="ml-1 text-xs font-bold">{cart.items_count}</span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
            >
              <Globe className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Stats bar */}
      <section className="border-b border-border bg-secondary/30 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-6 text-sm">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" /> {events.length} {t.events}
          </span>
          <span className="flex items-center gap-1">
            <Camera className="w-4 h-4" /> {totalSeasonPhotos} {t.photos}
          </span>
          <span className="flex items-center gap-1">
            <Dog className="w-4 h-4" /> {dogs.length} {t.dogs}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" /> {teams.length} {t.teams}
          </span>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-border px-6">
        <div className="max-w-7xl mx-auto flex gap-1">
          {[
            { key: 'events', icon: Calendar, pl: 'Zawody', en: 'Events' },
            { key: 'photos', icon: Grid3X3, pl: 'Zdjecia', en: 'Photos' },
            { key: 'dogs', icon: Dog, pl: 'Psy', en: 'Dogs' },
            { key: 'teams', icon: Users, pl: 'Druzyny', en: 'Teams' },
          ].map(tb => (
            <button
              key={tb.key}
              onClick={() => { setTab(tb.key); setSearchParams({ tab: tb.key }); }}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === tb.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tb.icon className="w-4 h-4" />
              {language === 'pl' ? tb.pl : tb.en}
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">

        {/* Events Tab */}
        {tab === 'events' && (
          <>
            {events.length === 0 ? (
              <div className="text-center py-20">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {language === 'pl' ? 'Brak zawodow w tym sezonie' : 'No events in this season'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event, idx) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Link to={`/sport/event/${event.id}`}>
                      <Card className="hover:border-primary/30 transition-all cursor-pointer group">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="text-lg font-semibold group-hover:text-primary transition-colors">
                                {event.name}
                              </h3>
                              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                <MapPin className="w-3 h-3" />
                                {event.location || (language === 'pl' ? 'Brak lokalizacji' : 'No location')}
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${statusBadge(event.status)}`}>
                              {event.status}
                            </span>
                          </div>

                          <div className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
                            <Calendar className="w-3 h-3" />
                            {event.date_start ? new Date(event.date_start).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US') : '--'}
                            {event.date_end && event.date_end !== event.date_start && (
                              <> -- {new Date(event.date_end).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US')}</>
                            )}
                          </div>

                          <div className="flex items-center justify-between text-sm">
                            <div className="flex gap-4">
                              <span className="flex items-center gap-1">
                                <Camera className="w-3 h-3" /> {event.photo_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <Dog className="w-3 h-3" /> {event.dog_count}
                              </span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Photos Tab — full gallery with all filters */}
        {tab === 'photos' && (
          <div>
            <SportGalleryToolbar
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sortBy={sortBy}
              onSortChange={(v) => { setSortBy(v); setPage(1); }}
              filterRatings={filterRatings}
              onRatingsChange={(v) => { setFilterRatings(v); setPage(1); }}
              filenameSearch={filenameSearch}
              onFilenameSearchChange={(v) => { setFilenameSearch(v); setPage(1); }}
              filterDogId={filterDogId}
              onDogIdChange={(v) => { setFilterDogId(v); setPage(1); }}
              filterTeamId={filterTeamId}
              onTeamIdChange={(v) => { setFilterTeamId(v); setPage(1); }}
              dateStart={dateStart}
              onDateStartChange={(v) => { setDateStart(v); setPage(1); }}
              dateEnd={dateEnd}
              onDateEndChange={(v) => { setDateEnd(v); setPage(1); }}
              pageSize={pageSize}
              onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
              events={events}
              filterEventId={filterEventId}
              onEventIdChange={(v) => { setFilterEventId(v); setFilterCompetitionDay(''); setPage(1); }}
              photographers={photographers}
              filterPhotographerId={filterPhotographerId}
              onPhotographerIdChange={(v) => { setFilterPhotographerId(v); setPage(1); }}
              competitors={competitors}
              filterCompetitorId={filterCompetitorId}
              onCompetitorIdChange={(v) => { setFilterCompetitorId(v); setPage(1); }}
              cameras={cameras}
              filterCamera={filterCamera}
              onCameraChange={(v) => { setFilterCamera(v); setPage(1); }}
              filterBurstType={filterBurstType}
              onBurstTypeChange={(v) => { setFilterBurstType(v); setPage(1); }}
              filterTimeStart={filterTimeStart}
              onTimeStartChange={(v) => { setFilterTimeStart(v); setPage(1); }}
              filterTimeEnd={filterTimeEnd}
              onTimeEndChange={(v) => { setFilterTimeEnd(v); setPage(1); }}
              competitionDays={(() => {
                if (!filterEventId) return [];
                const ev = events.find(e => e.id === filterEventId);
                if (!ev || !ev.date_start) return [];
                const days = [];
                const start = new Date(ev.date_start);
                const end = ev.date_end ? new Date(ev.date_end) : start;
                for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                  days.push({ date: d.toISOString().split('T')[0] });
                }
                return days;
              })()}
              filterCompetitionDay={filterCompetitionDay}
              onCompetitionDayChange={(v) => { setFilterCompetitionDay(v); setPage(1); }}
              total={total}
              dogs={dogs}
              teams={teams}
              language={language}
            />

            {photosLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-20">
                <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">{t.noPhotos}</p>
              </div>
            ) : (
              <>
                {/* Top pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mb-4">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      {t.prev}
                    </Button>
                    <span className="flex items-center text-sm text-muted-foreground px-3">
                      {page} / {totalPages}
                    </span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      {t.next}
                    </Button>
                  </div>
                )}

                <SportPhotoGrid
                  photos={photos}
                  viewMode={viewMode}
                  cartPhotoIds={cartPhotoIds}
                  addingToCart={addingToCart}
                  favoriteIds={favoriteIds}
                  onToggleFavorite={toggleFavorite}
                  pricing={pricing}
                  onAddToCart={handleAddToCart}
                  onPhotoClick={(idx) => { setCurrentPhotoIndex(idx); setLightboxOpen(true); }}
                  t={t}
                />

                {/* Bottom pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      {t.prev}
                    </Button>
                    <span className="flex items-center text-sm text-muted-foreground px-3">
                      {page} / {totalPages}
                    </span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      {t.next}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Dogs Tab */}
        {tab === 'dogs' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {dogs.map((dog, idx) => (
              <motion.div
                key={dog.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
              >
                <Card className="hover:border-primary/30 transition-all group">
                  <CardContent className="p-4">
                    <Link to={`/sport/dog/${dog.id}`} className="text-center block">
                      <Dog className="w-10 h-10 mx-auto mb-2 text-muted-foreground group-hover:text-primary transition-colors" />
                      <h4 className="font-medium">{dog.name || `#${dog.id.slice(0, 6)}`}</h4>
                      {dog.breed && <p className="text-xs text-muted-foreground">{dog.breed}</p>}
                      {dog.team_name && <p className="text-xs text-muted-foreground mt-1">{dog.team_name}</p>}
                      <p className="text-xs text-muted-foreground mt-2">
                        {dog.photo_count || 0} {t.photos}
                      </p>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Teams Tab */}
        {tab === 'teams' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {teams.map((team, idx) => (
              <motion.div
                key={team.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link to={`/sport/team/${team.id}`}>
                  <Card className="hover:border-primary/30 transition-all cursor-pointer group">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <Users className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
                        <div>
                          <h4 className="font-semibold">{team.name}</h4>
                          {team.club && <p className="text-sm text-muted-foreground">{team.club}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && photos.length > 0 && (
          <SportLightbox
            photos={photos}
            currentIndex={currentPhotoIndex}
            onIndexChange={setCurrentPhotoIndex}
            onClose={() => setLightboxOpen(false)}
            cartPhotoIds={cartPhotoIds}
            favoriteIds={favoriteIds}
            onToggleFavorite={toggleFavorite}
            addingToCart={addingToCart}
            pricing={pricing}
            onAddToCart={handleAddToCart}
            language={language}
            t={t}
          />
        )}
      </AnimatePresence>

      {/* Cart Drawer */}
      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50"
              onClick={() => { setCartOpen(false); setCheckoutOpen(false); }}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-background border-l border-border z-50 flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  <h2 className="text-lg font-bold">{t.cart}</h2>
                  <span className="text-sm text-muted-foreground">({cart.items_count} {t.items})</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setCartOpen(false); setCheckoutOpen(false); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                {cart.items.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground">{t.emptyCart}</p>
                  </div>
                ) : checkoutOpen ? (
                  <div className="space-y-4">
                    <h3 className="font-semibold">{t.buyerInfo}</h3>
                    <div>
                      <label className="text-sm text-muted-foreground">{t.name} *</label>
                      <input type="text" value={buyerName} onChange={e => setBuyerName(e.target.value)}
                        className="w-full mt-1 px-3 py-2 bg-secondary border border-border rounded-md text-sm" placeholder="Jan Kowalski" />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">{t.email} *</label>
                      <input type="email" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)}
                        className="w-full mt-1 px-3 py-2 bg-secondary border border-border rounded-md text-sm" placeholder="jan@example.com" />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">{t.phone}</label>
                      <input type="tel" value={buyerPhone} onChange={e => setBuyerPhone(e.target.value)}
                        className="w-full mt-1 px-3 py-2 bg-secondary border border-border rounded-md text-sm" placeholder="+48 123 456 789" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cart.items.map(item => (
                      <div key={item.photo_id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30">
                        {item.thumbnail ? (
                          <img src={item.thumbnail} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-14 h-14 rounded bg-secondary flex items-center justify-center flex-shrink-0">
                            <Camera className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono truncate">{item.filename || item.photo_id.slice(0, 12)}</p>
                          <span className="text-sm font-medium">{item.price} PLN</span>
                        </div>
                        <Button variant="ghost" size="sm" className="text-destructive flex-shrink-0" onClick={() => handleRemoveFromCart(item.photo_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {cart.items.length > 0 && (
                <div className="border-t border-border px-6 py-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{t.total}</span>
                    <span className="text-xl font-bold">{cart.total} {cart.currency}</span>
                  </div>
                  {checkoutOpen ? (
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => setCheckoutOpen(false)}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> {t.cart}
                      </Button>
                      <Button className="flex-1" disabled={!buyerName || !buyerEmail || checkoutLoading} onClick={handleCheckout}>
                        {checkoutLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {t.ordering}</> : <><CreditCard className="w-4 h-4 mr-1" /> {t.pay}</>}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleClearCart}>
                        <Trash2 className="w-4 h-4 mr-1" /> {t.clearCart}
                      </Button>
                      <Button className="flex-1" onClick={() => setCheckoutOpen(true)}>
                        <CreditCard className="w-4 h-4 mr-1" /> {t.checkout}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
